import type { EntityAdminClass } from '.';
import type { EntityAdminOperationSuccessParams, EntityAdminRecord, HandleCustomSaveEntityParams } from './types';

/**
 * Determines if we're dealing with a child entity.
 * Checks both addingChildEntity flag and entityClasses to handle both create and update scenarios.
 */
function getIsChildEntity<TRecord extends EntityAdminRecord>(
  addingChildEntity: boolean,
  entityClasses?: EntityAdminClass<TRecord>[] | undefined
): boolean {
  return addingChildEntity || (entityClasses?.[0]?.isChildRow ?? false);
}

/**
 * Handles custom save operations for entities using a provided custom handler.
 *
 * @param params - Parameters object containing the custom save handler, entity data, and operation handlers
 * @returns Promise resolving to true if custom save was handled, false otherwise
 */
export async function handleCustomSaveEntity<
  TRecord extends EntityAdminRecord,
  TDrawerRecord extends EntityAdminRecord,
>(params: HandleCustomSaveEntityParams<TRecord, TDrawerRecord>): Promise<boolean> {
  const {
    onCustomSaveEntity,
    newEntity,
    entityClasses,
    parentEntity,
    addingChildEntity,
    entityName,
    childEntityName,
    getEntityAsDrawerEntity,
    handleOperationSuccess,
    handleOperationSettled,
    handleOperationError,
    ensureRowVisibleById,
  } = params;

  const isCreate = entityClasses == null;
  const isChildEntity = getIsChildEntity(addingChildEntity, entityClasses);

  const convertedParentEntity = parentEntity ? getEntityAsDrawerEntity(parentEntity) : undefined;
  // Convert entityClasses to drawer entities (dirty entities)
  // For new child entities, include parentEntity in dirtyEntities if it exists
  const dirtyEntities: TDrawerRecord[] = entityClasses
    ? entityClasses.map(entityClass => getEntityAsDrawerEntity(entityClass.data, entityClass))
    : convertedParentEntity
      ? [convertedParentEntity]
      : [];

  try {
    const handled = await onCustomSaveEntity(dirtyEntities, newEntity, {
      isCreate,
      parentEntity,
      ensureRowVisibleById,
    });

    if (handled === true) {
      const operation: EntityAdminOperationSuccessParams = {
        action: isCreate ? 'create' : 'save',
        isChildEntity,
        entityName,
        childEntityName,
      };
      handleOperationSuccess(operation);
      handleOperationSettled(operation);
    }

    return handled === true;
  } catch (error) {
    handleOperationError({
      action: isCreate ? 'create' : 'save',
      isChildEntity,
      entityName,
      childEntityName,
      error,
    });
    handleOperationSettled({
      action: isCreate ? 'create' : 'save',
      isChildEntity,
      entityName,
      childEntityName,
    });
    throw error;
  }
}
